import * as R from 'ramda/src/index';
import moment from 'moment';
import {getTypeNextDayArrival} from 'helpers/time';

const baggagesCount = R.pipe(
  R.pluck('services'),
  R.pluck('baggageAllowance'),
  R.apply(Math.min)
);

const getTotalFromPassengers = R.pipe(
  R.values,
  R.map(({count, price}) => count * price.total),
  R.sum
);

const getTransportationTypes = R.pipe(
  R.pluck('transport'),
  R.pluck('type'),
  R.uniq
);

const getTransportationTypesPerResult = R.pipe(
  R.pluck('segments'),
  R.flatten,
  R.pluck('transport'),
  R.pluck('type'),
  R.uniq
);

const getOperatingAirlines = (carriers) => R.pipe(
  R.pluck('segments'),
  R.flatten,
  R.pluck('transport'),
  R.pluck('carriers'),
  R.pluck('operating'),
  R.uniq,
  R.map((item) => getData(item)(carriers))
);

const hasDifferentAirport = R.pipe(
  R.pluck('differentAirport'),
  R.includes(true)
);

const countTechnicalStops = R.pipe(
  R.pluck('route'),
  R.pluck('technicalStop'),
  R.flatten,
  R.pluck('duration'),
  R.reject(R.isNil)
);

const checkIsRoundTrip = (legs) => {
  const getFirstLegFirstSegment = R.pipe(
    R.head,
    R.prop('segments'),
    R.head,
    R.prop('route')
  );
  const getLastLegLastSegment = R.pipe(
    R.last,
    R.prop('segments'),
    R.last,
    R.prop('route')
  );

  return (legs.length > 1)
    && getFirstLegFirstSegment(legs).departure.location === getLastLegLastSegment(legs).arrival.location;
};

const getFirstDepLocation = R.pipe(
  R.prop('segments'),
  R.head,
  R.path(['route', 'departure', 'location'])
);

const getLastDepLocation = R.pipe(
  R.prop('segments'),
  R.last,
  R.path(['route', 'arrival', 'location'])
);

const checkDepPreviousLegArr = (legs, legIndex) => {
  if (legIndex === 0) {
    return false;
  }
  const legDeparture = getFirstDepLocation(legs[legIndex]);
  const previousLegArrival = getLastDepLocation(legs[legIndex - 1]);
  return Boolean(legDeparture !== previousLegArrival);
};

const hasLegDifferentAirport = ({legs, legIndex}) => {
  const checkLeg = (legs, legIndex) => {
    const hasNextLeg = legIndex + 1 < legs.length;
    if (!hasNextLeg) {
      return false;
    }
    const legLastSegmentArrival = getLastDepLocation(legs[legIndex]);
    const nextLegDeparture = getFirstDepLocation(legs[legIndex + 1]);
    return Boolean(legLastSegmentArrival !== nextLegDeparture);
  };

  return {
    arrival: checkLeg(legs, legIndex),
    departure: legIndex !== 0 && checkDepPreviousLegArr(legs, legIndex)
  };
};

const depDifferentFromArr = ({legs, legIndex, segmentIndex}) => {
  return segmentIndex === 0 && legIndex !== 0 && checkDepPreviousLegArr(legs, legIndex);
};

const buildMinMetrics = (legs, price) => {
  const getDepDate = R.pipe(
    R.prop('segments'),
    R.head,
    R.path(['route', 'departure', 'datetime'])
  );
  const getArrDate = R.pipe(
    R.prop('segments'),
    R.last,
    R.path(['route', 'arrival', 'datetime'])
  );
  const metrics = legs.reduce((acc, leg, index) => {
    acc[`legDuration-${index}`] = leg.duration;
    acc[`legDepDate-${index}`] = new Date(getDepDate(leg)).getTime();
    acc[`legArrDate-${index}`] = new Date(getArrDate(leg)).getTime();
    return acc;
  }, {});

  return {
    ...metrics,
    price
  };
};

const getData = (datum) => R.pipe(
  R.values,
  R.find(
    R.propEq('code', datum)
  )
);

const getRouteData = (route, locations) => (
  {
    ...route,
    arrival: {
      ...route.arrival,
      datetime: moment(route.arrival.datetime),
      location: {
        ...getData(route.arrival.location)(locations)
      }
    },
    departure: {
      ...route.departure,
      datetime: moment(route.departure.datetime),
      location: {
        ...getData(route.departure.location)(locations)
      }
    }
  }
);

class Result {
  constructor({passengers, legs, resources, groupId, provider, id, resourceId}) {
    const total = getTotalFromPassengers(passengers);
    const result = {
      groupId,
      minMetrics: buildMinMetrics(legs, total),
      transportationTypes: getTransportationTypesPerResult(legs),
      checkLastSeats: R.pipe(
        R.pluck('services'),
        R.pluck('numberOfSeats'),
        R.apply(Math.min)
      )(legs),
      isRoundTrip: checkIsRoundTrip(legs),
      providers: [{
        provider, id, resourceId, total
      }],
      airlines: getOperatingAirlines(resources.carriers)(legs),
      legs: legs.map((leg, legIndex) => {
        const segments = leg.segments;
        const {carriers, cabinClasses, equipments, locations} = resources;
        const newSegments = leg.segments
          .map((segment, segmentIndex) => {
            const {route, transport, services} = segment;
            const isLast = R.equals(segment, R.last(segments));
            const arrDatetime = moment(route.arrival.datetime);
            const depDatetimeNextSeg = isLast
              ? arrDatetime
              : moment(segments[segmentIndex + 1].route.departure.datetime);
            return {
              ...segment,
              isFirst: R.equals(segment, R.head(segments)),
              isLast,
              route: {
                ...getRouteData(route, locations),
                technicalStop: route.technicalStop.map((technical) =>
                  (getRouteData(technical, locations)))
              },
              transport: {
                ...transport,
                equipment: R.pathOr('', [transport.equipment, 'name'], equipments),
                carriers: {
                  ...transport.carriers,
                  operating: getData(transport.carriers.operating)(carriers),
                  marketing: getData(transport.carriers.marketing)(carriers)
                }
              },
              services: {
                ...services,
                cabinClass: getData(services.cabinClass)(cabinClasses)
              },
              hasTechnicalStop: !R.isEmpty(route.technicalStop),
              stopDuration: moment.duration(depDatetimeNextSeg.diff(arrDatetime)),
              differentAirport: Boolean(
                segments[segmentIndex + 1]
                && (route.arrival.location !== segments[segmentIndex + 1].route.departure.location)
              ),
              ...getTypeNextDayArrival(
                new Date(R.path(['departure', 'datetime'], route)),
                new Date(R.path(['arrival', 'datetime'], route))
              ),
              isDepDifferentFromPreviousArr:
                depDifferentFromArr({legs, legIndex, segmentIndex})
            };
          });
        return {
          ...leg,
          transportationTypes: getTransportationTypes(segments),
          hasTechnicalStop: countTechnicalStops(newSegments).length > 0,
          hasDifferentAirport: hasDifferentAirport(newSegments),
          legDifferentAirport: hasLegDifferentAirport({legs: legs, legIndex}),
          ...getTypeNextDayArrival(
            new Date(R.path(['route', 'departure', 'datetime'], R.head(newSegments))),
            new Date(R.path(['route', 'arrival', 'datetime'], R.last(newSegments)))
          ),
          baggagesCount: baggagesCount(newSegments),
          segments: newSegments,
          stopsCount: (newSegments.length - 1) + countTechnicalStops(newSegments).length
        };
      })
    };

    Object.assign(this, result);
  }
}

export default Result;
